import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { PROJECT_MANAGEMENT_URL } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerService {
    private readonly CUSTOMER_API = `${PROJECT_MANAGEMENT_URL}/customer`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    /**
     * Get all customers with pagination and search
     */
    public getAllCustomers(params: {
        pageIndex?: number;
        pageSize?: number;
        searchFilter?: string;
        countryIds?: number[];
        customerIdFilter?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();

        if (params.pageIndex !== undefined) {
            queryParams = queryParams.set('pageIndex', params.pageIndex);
        }
        if (params.pageSize !== undefined) {
            queryParams = queryParams.set('pageSize', params.pageSize);
        }
        if (params.searchFilter && params.searchFilter.trim() !== '') {
            queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
        }
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.customerIdFilter && params.customerIdFilter !== 'all') {
            queryParams = queryParams.set('customerIdFilter', params.customerIdFilter);
        }

        return this.http
            .get<any>(`${this.CUSTOMER_API}/getAll`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export customers to Excel
     */
    public exportExcel(params: {
        searchFilter?: string;
        countryIds?: number[];
        includeCustomerId: boolean;
        customerIdFilter?: string;
    }): Observable<Blob> {
        let queryParams = new HttpParams();

        if (params.searchFilter && params.searchFilter.trim() !== '') {
            queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
        }
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        queryParams = queryParams.set('includeCustomerId', params.includeCustomerId.toString());

        if (params.customerIdFilter && params.customerIdFilter !== 'all') {
            queryParams = queryParams.set('customerIdFilter', params.customerIdFilter);
        }

        return this.http.get(`${this.CUSTOMER_API}/downloadExcelReport`, {
            params: queryParams,
            responseType: 'blob'
        });
    }

    /**
     * Download customer template
     */
    public downloadTemplate(params?: {
        searchFilter?: string;
        countryIds?: number[];
    }): Observable<Blob> {
        let queryParams = new HttpParams();

        if (params) {
            if (params.searchFilter && params.searchFilter.trim() !== '') {
                queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
            }
            if (params.countryIds && params.countryIds.length > 0) {
                queryParams = queryParams.set('countryIds', params.countryIds.join(','));
            }
        }

        return this.http.get(`${this.CUSTOMER_API}/downloadExcelTemplate`, {
            params: queryParams,
            responseType: 'blob'
        });
    }

    /**
     * Import customer data
     */
    public importData(data: any[]): Observable<{ message: string; errors: string[] }> {
        return this.http
            .post<{ message: string; errors: string[] }>(`${this.CUSTOMER_API}/importData`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Get next reference number for a country
     */
    public getNextReferenceNumber(countryId: number): Observable<{ referenceNo: string }> {
        const params = new HttpParams().set('countryId', countryId.toString());
        return this.http
            .get<{ referenceNo: string }>(`${this.CUSTOMER_API}/getNextReferenceNumber`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Get all customers for dropdown (with search and country filter)
     */
    public getAllCustomersForDropdown(searchFilter?: string, countryId?: number, countryIds?: number[]): Observable<{ result: { id: number; customerId: string; name: string }[] }> {
        let queryParams = new HttpParams();
        if (searchFilter && searchFilter.trim() !== '') {
            queryParams = queryParams.set('searchFilter', searchFilter.trim());
        }
        // If countryIds array is provided, use it (takes precedence over single countryId)
        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', countryIds.join(','));
        } else if (countryId !== undefined && countryId !== null) {
            // Fallback to single countryId for backward compatibility
            queryParams = queryParams.set('countryId', countryId.toString());
        }
        return this.http
            .get<{ result: { id: number; customerId: string; name: string }[] }>(`${this.CUSTOMER_API}/getCustomerByCountryId`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Get customer by ID
     */
    public getCustomerById(id: number): Observable<any> {
        return this.http
            .get<any>(`${this.CUSTOMER_API}/getById/${id}`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Create a new customer
     */
    public createCustomer(data: {
        referenceNo?: string;
        customerId: string;
        name: string;
        registerDate: string;
        countryId: number;
        addresses?: Array<{
            addressTypeId: number;
            address: string;
            countryId?: number;
        }>;
        contactDetails?: Array<{
            name: string;
            email?: string;
            phone?: string;
        }>;
    }): Observable<any> {
        return this.http
            .post<any>(`${this.CUSTOMER_API}/create`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Update an existing customer
     */
    public updateCustomer(id: number, data: {
        referenceNo?: string;
        customerId: string;
        name: string;
        registerDate: string;
        countryId: number;
        addresses?: Array<{
            addressTypeId: number;
            address: string;
            countryId?: number;
        }>;
        contactDetails?: Array<{
            name: string;
            email?: string;
            phone?: string;
        }>;
    }): Observable<any> {
        return this.http
            .put<any>(`${this.CUSTOMER_API}/update/${id}`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Delete a customer (soft delete)
     */
    public deleteCustomer(id: number): Observable<{ message: string }> {
        return this.http
            .delete<{ message: string }>(`${this.CUSTOMER_API}/delete/${id}`)
            .pipe(catchError(this.errorHandler.handleError));
    }
}

